import { MESSAGE_TYPE } from 'shared/constants/messages';
import { applyPageFilters, applyRoleFilter, sortComparator } from './helpers';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import { matchesFilters } from './helpers/filterHelpers';
import {
  getUserPermissions,
  getUserRole,
} from '../../../helper/permissionsHelper';
import camelcaseKeys from 'camelcase-keys';
import {
  conversationMatchesAdminAssigneeScope,
  conversationMatchesKanban,
  conversationMatchesMine,
  conversationMatchesUnassigned,
} from 'dashboard/components/widgets/conversation/conversationQuickFilters';

export const getSelectedChatConversation = ({
  allConversations,
  selectedChatId,
}) =>
  allConversations.filter(conversation => conversation.id === selectedChatId);

const pageFiltersFor = (filters, currentUserId) => ({
  ...filters,
  currentUserId,
});

const getters = {
  getAllConversations: ({ allConversations, chatSortFilter: sortKey }) => {
    return allConversations.sort((a, b) => sortComparator(a, b, sortKey));
  },
  getFilteredConversations: (
    { allConversations, chatSortFilter, appliedFilters, conversationFilters },
    _,
    __,
    rootGetters
  ) => {
    const currentUser = rootGetters.getCurrentUser;
    const currentUserId = rootGetters.getCurrentUser.id;
    const currentAccountId = rootGetters.getCurrentAccountId;

    const permissions = getUserPermissions(currentUser, currentAccountId);
    const userRole = getUserRole(currentUser, currentAccountId);

    const adminAssigneeScope = conversationFilters?.adminAssigneeScope;

    return allConversations
      .filter(conversation => {
        const matchesFilterResult = matchesFilters(
          conversation,
          appliedFilters
        );
        const allowedForRole = applyRoleFilter(
          conversation,
          userRole,
          permissions,
          currentUserId
        );
        const allowedForAdminScope = conversationMatchesAdminAssigneeScope(
          conversation,
          currentUserId,
          adminAssigneeScope,
          { isAdministrator: userRole === 'administrator' }
        );

        return matchesFilterResult && allowedForRole && allowedForAdminScope;
      })
      .sort((a, b) => sortComparator(a, b, chatSortFilter));
  },
  getSelectedChat: ({ selectedChatId, allConversations }) => {
    const selectedChat = allConversations.find(
      conversation => conversation.id === selectedChatId
    );
    return selectedChat || {};
  },
  getSelectedChatAttachments: ({ selectedChatId, attachments }) => {
    return attachments[selectedChatId] || [];
  },
  getSelectedChatAttachmentsLoaded: ({ selectedChatId, attachments }) =>
    selectedChatId !== null && attachments[selectedChatId] !== undefined,
  getChatListFilters: ({ conversationFilters }) => conversationFilters,
  getLastEmailInSelectedChat: (stage, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    const { messages = [] } = selectedChat;
    const lastEmail = [...messages].reverse().find(message => {
      const { message_type: messageType } = message;
      if (message.private) return false;

      return [MESSAGE_TYPE.OUTGOING, MESSAGE_TYPE.INCOMING].includes(
        messageType
      );
    });

    return lastEmail;
  },
  getMineChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUserID = rootGetters.getCurrentUser?.id;

    return _state.allConversations.filter(conversation => {
      const shouldFilter = applyPageFilters(
        conversation,
        pageFiltersFor(activeFilters, currentUserID)
      );
      // SiteUp v654: Mine = assignee OR participant (aligned with ConversationFinder).
      return (
        shouldFilter && conversationMatchesMine(conversation, currentUserID)
      );
    });
  },
  getAppliedConversationFiltersV2: _state => {
    // TODO: Replace existing one with V2 after migrating the filters to use camelcase
    return _state.appliedFilters.map(camelcaseKeys);
  },
  getAppliedConversationFilters: _state => {
    return _state.appliedFilters;
  },
  getAppliedConversationFiltersQuery: _state => {
    const hasAppliedFilters = _state.appliedFilters.length !== 0;
    return hasAppliedFilters ? filterQueryGenerator(_state.appliedFilters) : [];
  },
  getUnAssignedChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUserId = rootGetters?.getCurrentUser?.id;
    return _state.allConversations.filter(conversation => {
      const shouldFilter = applyPageFilters(
        conversation,
        pageFiltersFor(activeFilters, currentUserId)
      );
      // SiteUp v654: Unassigned = no assignee AND no participants (fail-closed).
      return shouldFilter && conversationMatchesUnassigned(conversation);
    });
  },
  // Kanban preset membership against the shared conversation cache (realtime-safe).
  getKanbanChats:
    (_state, _, __, rootGetters) =>
    (activeFilters, kanbanScope = {}) => {
      const currentUserId = rootGetters?.getCurrentUser?.id;
      return _state.allConversations.filter(conversation => {
        const shouldFilter = applyPageFilters(
          conversation,
          pageFiltersFor(activeFilters, currentUserId)
        );
        return (
          shouldFilter &&
          conversationMatchesKanban(conversation, {
            funnelId: kanbanScope.funnelId || kanbanScope.kanbanFunnelId || '',
            stageKey: kanbanScope.stageKey || kanbanScope.kanbanStage || '',
          })
        );
      });
    },
  getParticipatingChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUserId = rootGetters.getCurrentUser?.id;
    const getWatchers = rootGetters['conversationWatchers/getByConversationId'];
    return _state.allConversations.filter(conversation => {
      const watchers = getWatchers(conversation.id);
      // Watchers are only loaded for the conversation open in the detail
      // panel. If loaded and current user is not in them, filter it out.
      if (watchers && !watchers.some(w => w.id === currentUserId)) {
        return false;
      }
      return applyPageFilters(
        conversation,
        pageFiltersFor(activeFilters, currentUserId)
      );
    });
  },
  getAllStatusChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUser = rootGetters.getCurrentUser;
    const currentUserId = rootGetters.getCurrentUser.id;
    const currentAccountId = rootGetters.getCurrentAccountId;

    const permissions = getUserPermissions(currentUser, currentAccountId);
    const userRole = getUserRole(currentUser, currentAccountId);

    const { adminAssigneeScope } = activeFilters;

    return _state.allConversations.filter(conversation => {
      const shouldFilter = applyPageFilters(
        conversation,
        pageFiltersFor(activeFilters, currentUserId)
      );
      const allowedForRole = applyRoleFilter(
        conversation,
        userRole,
        permissions,
        currentUserId
      );
      const allowedForAdminScope = conversationMatchesAdminAssigneeScope(
        conversation,
        currentUserId,
        adminAssigneeScope,
        { isAdministrator: userRole === 'administrator' }
      );

      return shouldFilter && allowedForRole && allowedForAdminScope;
    });
  },
  getChatListLoadingStatus: ({ listLoadingStatus }) => listLoadingStatus,
  getChatListLoadError: ({ listLoadError }) => listLoadError === true,
  getUnavailableConversationId: ({ unavailableConversationId }) =>
    unavailableConversationId || null,
  getAllMessagesLoaded(_state) {
    const [chat] = getSelectedChatConversation(_state);
    return !chat || chat.allMessagesLoaded === undefined
      ? false
      : chat.allMessagesLoaded;
  },
  getUnreadCount(_state) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return [];
    return chat.messages.filter(
      chatMessage =>
        chatMessage.created_at * 1000 > chat.agent_last_seen_at * 1000 &&
        chatMessage.message_type === 0 &&
        chatMessage.private !== true
    ).length;
  },
  getChatStatusFilter: ({ chatStatusFilter }) => chatStatusFilter,
  getChatSortFilter: ({ chatSortFilter }) => chatSortFilter,
  getSelectedInbox: ({ currentInbox }) => currentInbox,
  getConversationById: _state => conversationId => {
    return _state.allConversations.find(
      value => value.id === Number(conversationId)
    );
  },
  getConversationParticipants: _state => {
    return _state.conversationParticipants;
  },
  getConversationLastSeen: _state => {
    return _state.conversationLastSeen;
  },

  getContextMenuChatId: _state => {
    return _state.contextMenuChatId;
  },

  getCopilotAssistant: _state => {
    return _state.copilotAssistant;
  },
};

export default getters;
